import { useState } from 'react';
import {
  CommandValidationError,
  STOP_FAIL_LABELS,
  buildFailPayload,
  failureProblems,
  type StopFailReason,
} from '@ecobox/firebase';
import { STOP_FAIL_REASONS } from '@ecobox/schema';
import { Button, Card, Field, Select, Textarea, useToast } from '@ecobox/ui';
import { PhotoField } from '../../components/PhotoField';
import { useGeo } from '../../hooks/useGeo';
import { useServices } from '../../services';

/** The stop could not be done: a reason, a photo and a note (`failStop` / `failPickup`, 04 §4.5). */
export function FailForm({
  runId,
  stopId,
  type,
  onCancel,
}: {
  runId: string;
  stopId: string;
  type: 'failStop' | 'failPickup';
  onCancel: () => void;
}) {
  const { writer, uploads } = useServices();
  const toast = useToast();
  const { fix } = useGeo(false);
  const [reason, setReason] = useState<StopFailReason | ''>('');
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [problems, setProblems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const found = failureProblems({ reason, note, photoCount: photos.length });
    setProblems(found);
    if (found.length > 0) return;
    setBusy(true);
    try {
      const geo = await Promise.race([
        fix(),
        new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), 3000)),
      ]);
      const photoPaths: string[] = [];
      for (const photo of photos)
        photoPaths.push(await uploads.enqueueUpload(photo, { kind: 'pod' }));
      writer.send(
        type,
        buildFailPayload({
          type,
          runId,
          stopId,
          draft: { reason, note, photoCount: photos.length },
          photoPaths,
          geo,
        }),
      );
      toast.show({
        title: 'Saved',
        message: 'The failed attempt is saved. It goes to the server when you have signal.',
        tone: 'success',
      });
    } catch (err) {
      setProblems([
        err instanceof CommandValidationError && err.issues.length > 0
          ? err.issues.join('; ')
          : err instanceof Error
            ? err.message
            : String(err),
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="It could not be done">
      <div className="ops-stack">
        <Field label="What happened?" required>
          {(p) => (
            <Select
              {...p}
              size="touch"
              value={reason}
              onChange={(e) => setReason(e.target.value as StopFailReason | '')}
            >
              <option value="">Choose…</option>
              {STOP_FAIL_REASONS.map((r) => (
                <option key={r} value={r}>
                  {STOP_FAIL_LABELS[r]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <PhotoField
          label="Photo (the door, the address)"
          files={photos}
          onChange={setPhotos}
          max={5}
        />
        <Field label="Note">
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>
        {problems.length > 0 ? (
          <ul role="alert" className="ops-error" style={{ margin: 0, paddingLeft: 18 }}>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        ) : null}
        <Button size="touch" variant="danger" block loading={busy} onClick={() => void submit()}>
          Save the failed attempt
        </Button>
        <Button size="touch" variant="secondary" block onClick={onCancel}>
          Back
        </Button>
      </div>
    </Card>
  );
}
